import {
  FaAngleLeft,
  FaCircleNotch,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa6";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  getTest,
  postSaveTestResult,
  getAiModels,
  getTopMenuData,
} from "../../api/mainApi";
import ConfirmDialog from "../confirmDialog/ConfirmDialog";
import Premium from "../premium/Premium";

function Test() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testStarted, setTestStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [interpretation, setInterpretation] = useState(null);
  const [subscaleScores, setSubscaleScores] = useState({});
  const [subscaleInterpretations, setSubscaleInterpretations] = useState({});
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isMaxed, setIsMaxed] = useState(false);
  const [sparks, setSparks] = useState([]);
  const [points, setPoints] = useState(null);
  const [isUserPremium, setIsUserPremium] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(
    localStorage.getItem("selectedAiModel") || "Basic",
  );
  const [modelPrice, setModelPrice] = useState(
    localStorage.getItem("selectedAiModelPrice") || 10,
  );
  const [showPremium, setShowPremium] = useState(false);

  // 1. Завантаження самого тесту
  useEffect(() => {
    const fetchTest = async () => {
      try {
        const data = await getTest(id);
        setTest(data);
      } catch (error) {
        console.error("Error fetching test:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTest();
  }, [id]);

  // 2. Завантаження налаштувань (моделі та поінти)
  useEffect(() => {
    async function fetchModels() {
      const data = await getAiModels();
      if (!data?.aiModelsSnapshot) return;

      const sortedModels = Object.entries(data.aiModelsSnapshot).sort(
        (a, b) => a[1].quality - b[1].quality,
      );
      setModels(sortedModels);

      if (!localStorage.getItem("selectedAiModel")) {
        const defaultModel = sortedModels[0][0];
        const defaultPrice = sortedModels[0][1].price;

        setSelectedModel(defaultModel);
        setModelPrice(defaultPrice);

        localStorage.setItem("selectedAiModel", defaultModel);
        localStorage.setItem("selectedAiModelPrice", defaultPrice);
      } else {
        const storedModel = localStorage.getItem("selectedAiModel");
        if (storedModel && data.aiModelsSnapshot[storedModel]) {
          const price = data.aiModelsSnapshot[storedModel].price;

          setModelPrice(price);
          localStorage.setItem("selectedAiModelPrice", price);
        }
      }
    }

    const fetchTopMenuData = async () => {
      try {
        const data = await getTopMenuData();
        setPoints(data.points);
        setIsUserPremium(data.premium);
      } catch (error) {
        console.log("Помилка отримання TopMenuData:", error);
      }
    };

    fetchModels();
    fetchTopMenuData();
  }, []); // <-- Звідси я прибрав слухача подій

  // 3. ПРАВИЛЬНИЙ СЛУХАЧ ДЛЯ ТЕЛЕГРАМ КНОПКИ
  // Цей useEffect реагує на зміну testStarted та showResult
  useEffect(() => {
    const handleTgBack = () => {
      // Якщо тест почато і результату ще немає - питаємо
      if (testStarted && !showResult) {
        setShowExitDialog(true);
      } else {
        // Якщо тест ще не почався або ми вже на екрані результату - просто виходимо
        navigate("/tests", { replace: true });
      }
    };

    window.addEventListener("request-test-exit", handleTgBack);

    return () => {
      window.removeEventListener("request-test-exit", handleTgBack);
    };
  }, [testStarted, showResult, navigate]);

  const startTest = () => {
    setTestStarted(true);
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    const newAnswers = { ...answers, [questionId]: optionIndex };
    setAnswers(newAnswers);
  };

  const goToNext = async () => {
    if (currentQuestionIndex < test.items.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      let score = 0;
      test.items.forEach((item) => {
        const selectedOption = item.options[answers[item.id]];
        if (selectedOption) {
          score += selectedOption.score;
        }
      });
      setTotalScore(score);

      if (test.has_subscales) {
        const subscaleScoresObj = {};
        test.subscales.forEach((subscale) => {
          let subscaleScore = 0;
          subscale.item_ids.forEach((itemId) => {
            const item = test.items.find((i) => i.id === itemId);
            const selectedOption = item.options[answers[item.id]];
            if (selectedOption) {
              subscaleScore += selectedOption.score;
            }
          });
          subscaleScoresObj[subscale.id] = subscaleScore;
        });
        setSubscaleScores(subscaleScoresObj);

        const subscaleInterps = {};
        Object.keys(subscaleScoresObj).forEach((subscaleId) => {
          const subscaleScore = subscaleScoresObj[subscaleId];
          const subscaleScoring = test.scoring.subscales_scoring[subscaleId];
          const interp = subscaleScoring.interpretation.find(
            (range) =>
              subscaleScore >= range.min_score &&
              subscaleScore <= range.max_score,
          );
          subscaleInterps[subscaleId] = interp;
        });
        setSubscaleInterpretations(subscaleInterps);
      }

      const interp = test.scoring.total_score
        ? test.scoring.total_score.interpretation.find(
            (range) => score >= range.min_score && score <= range.max_score,
          )
        : test.scoring.interpretation.find(
            (range) => score >= range.min_score && score <= range.max_score,
          );
      setInterpretation(interp);

      try {
        await postSaveTestResult(id, score, answers);
      } catch (error) {
        console.error("Error saving test result:", error);
      }

      setShowResult(true);
    }
  };

  const goToPrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const backButton = () => {
    if (testStarted && !showResult) {
      setShowExitDialog(true);
    } else {
      navigate("/tests", { replace: true });
    }
  };

  const confirmExit = () => {
    setShowExitDialog(false);
    navigate("/tests", { replace: true });
  };

  const cancelExit = () => {
    setShowExitDialog(false);
  };

  const restartTest = () => {
    setTestStarted(false);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResult(false);
    setTotalScore(0);
    setInterpretation(null);
    setSubscaleScores({});
    setSubscaleInterpretations({});
  };

  useEffect(() => {
    if (!test) return;

    const currentProgress =
      ((currentQuestionIndex + 1) / test.items.length) * 100;

    if (currentProgress >= 0 && currentProgress < 50) {
      addSparks(3);
    } else if (currentProgress >= 50 && currentProgress < 75) {
      addSparks(5);
    } else if (currentProgress >= 75 && currentProgress <= 100) {
      addSparks(8);
    }
  }, [currentQuestionIndex, test]);

  const addSparks = (count) => {
    const newSparks = Array.from({ length: count }).map(() => ({
      id: Math.random(),
      x: Math.random() * 100 + "%",
      y: Math.random() * 100 + "%",
    }));
    setSparks((prev) => [...prev, ...newSparks]);
    setTimeout(() => {
      setSparks((prev) => prev.slice(count));
    }, 1000);
  };

  const getCurrentModel = () => {
    return models.find(([key]) => key === selectedModel)?.[1];
  };

  const canAffordAction = () => {
    const currentModel = getCurrentModel();
    return (
      points >= modelPrice || (isUserPremium && currentModel?.free_with_premium)
    );
  };

  const getMaxTotalScore = () => {
    if (!test?.items) return 0;
    return test.items.reduce((max, item) => {
      const itemMax = Math.max(...item.options.map((opt) => opt.score));
      return max + itemMax;
    }, 0);
  };

  const getScaleColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage < 33) return "bg-red-500";
    if (percentage < 66) return "bg-yellow-500";
    return "bg-emerald-500";
  };

  if (loading) {
    return (
      <div className="flex flex-col flex-1 h-screen overflow-hidden">
        <div className="flex justify-center items-center flex-1">
          <FaCircleNotch className="w-24 h-24 opacity-50 text-slate-950 animate-spin" />
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="flex flex-col flex-1 h-screen overflow-hidden">
        <div className="flex justify-center items-center flex-1">
          <p className="text-white text-lg">{t("tests.notFound")}</p>
        </div>
      </div>
    );
  }

  if (!testStarted) {
    return (
      <div className="flex flex-col flex-1 h-screen overflow-hidden">
        <div className="w-full pt-7 pb-2 px-20">
          <p className="text-lg font-semibold text-center text-white truncate w-full">
            {test.test_name}
          </p>
        </div>

        <div className="flex-1 flex flex-col justify-center">
          <div className="">
            <h2 className="text-xl font-bold text-white mb-4 text-center">
              {test.test_name}
            </h2>
            <p className="text-gray-300 mb-4 text-left">{test.description}</p>
            <p className="text-sm text-gray-400 mb-6 text-left">
              {test.instructions}
            </p>
            <div className="bg-slate-700 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-300 text-left">
                {test.disclaimer}
              </p>
            </div>
            <p className="text-sm text-gray-400 text-center">
              {t("tests.questionsCount")}: {test.items.length}
            </p>
          </div>
        </div>

        <button
          onClick={startTest}
          className="text-3xl font-bold rounded-lg py-2 w-full mb-4 bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move"
        >
          {t("tests.start")}
        </button>
      </div>
    );
  }

  if (showResult) {
    return (
      <div className="flex flex-col h-dvh -mx-4 -mt-4">
        <div className="flex justify-between items-center w-full p-4 pt-24 bg-gray-950">
          <button onClick={backButton} className="flex items-center">
            <FaAngleLeft className="w-7 h-7" />
          </button>
          <p className="text-2xl font-semibold">{t("tests.result")}</p>
          <div className="w-7 h-7"></div>
        </div>

        <div className="flex-1 text-start relative overflow-y-auto">
          <div className="rounded-lg p-6 w-full">
            <h2 className="text-xl text-center font-bold text-white mb-4">
              {test.test_name}
            </h2>

            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-300 mb-1 items-end">
                <span>0</span>
                <p className="text-xl font-bold text-emerald-400">
                  {t("tests.score")}: {totalScore}
                </p>
                <span>{getMaxTotalScore()}</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${getScaleColor(
                    totalScore,
                    getMaxTotalScore(),
                  )}`}
                  style={{
                    width: `${(totalScore / getMaxTotalScore()) * 100}%`,
                  }}
                ></div>
              </div>
            </div>

            {interpretation && (
              <>
                <p className="text-lg text-center font-semibold text-cyan-400 mb-2">
                  {interpretation.level}
                </p>
                <p className="text-sm text-gray-300 mb-4">
                  {interpretation.description}
                </p>
              </>
            )}

            <button
              onClick={() => {
                if (canAffordAction()) {
                  const subscaleMaxScores = {};
                  const subscaleNames = {};
                  if (test.has_subscales) {
                    test.subscales.forEach((subscale) => {
                      subscaleMaxScores[subscale.id] =
                        test.scoring.subscales_scoring[subscale.id]
                          ?.max_possible_score || 0;
                      subscaleNames[subscale.id] = subscale.name;
                    });
                  }

                  navigate("/ai-psychologist", {
                    state: {
                      testResult: {
                        score: totalScore,
                        minScore: 0,
                        maxScore: getMaxTotalScore(),
                        testName: test.test_name,
                        subscaleScores: subscaleScores,
                        subscaleMaxScores: subscaleMaxScores,
                        subscaleNames: subscaleNames,
                        testId: id,
                      },
                    },
                  });
                } else {
                  setShowPremium(true);
                }
              }}
              disabled={!canAffordAction()}
              className={`w-full text-xl font-bold rounded-lg py-2 ${
                canAffordAction()
                  ? "bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move"
                  : "bg-gray-600 cursor-not-allowed"
              }`}
            >
              {t("tests.aiAnalysis")}
            </button>
            {!canAffordAction() && (
              <p className="text-center text-sm text-gray-400">
                {t("tests.insufficientPoints")}
              </p>
            )}

            {test.has_subscales && Object.keys(subscaleScores).length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-white mb-3">
                  {t("tests.subscales")}
                </h3>
                {test.subscales.map((subscale) => {
                  const subscaleScore = subscaleScores[subscale.id];
                  const maxSubscaleScore =
                    test.scoring.subscales_scoring[subscale.id]
                      ?.max_possible_score || subscaleScore;
                  return (
                    <div
                      key={subscale.id}
                      className="bg-slate-800 rounded-lg p-4 mb-3"
                    >
                      <p className="text-sm text-center font-medium text-cyan-400 mb-1">
                        {subscale.name}
                      </p>
                      <p className="text-xs text-start text-gray-300 mb-2">
                        {subscale.description}
                      </p>

                      <div className="mb-2">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>0</span>
                          <p className="text-base font-bold text-emerald-400 items-end">
                            {t("tests.points")}: {subscaleScore}
                          </p>
                          <span>{maxSubscaleScore}</span>
                        </div>
                        <div className="w-full bg-gray-600 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${getScaleColor(
                              subscaleScore,
                              maxSubscaleScore,
                            )}`}
                            style={{
                              width: `${
                                (subscaleScore / maxSubscaleScore) * 100
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>

                      {subscaleInterpretations[subscale.id] && (
                        <>
                          <p className="text-sm text-center font-semibold text-cyan-400 mb-1">
                            {subscaleInterpretations[subscale.id].level}
                          </p>
                          <p className="text-xs text-gray-300">
                            {subscaleInterpretations[subscale.id].description}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex flex-col gap-4 w-full mt-6 mb-4">
              <button
                onClick={() => navigate("/tests")}
                className="text-xl font-bold rounded-lg py-2 bg-gray-700 text-white"
              >
                {t("tests.backToList")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentItem = test.items[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / test.items.length) * 100;
  const selectedOptionIndex = answers[currentItem.id];

  return (
    <div className="flex flex-col flex-1 h-screen overflow-hidden">
      <div className="flex justify-between items-center w-full pt-20">
        <button onClick={backButton}>
          <FaAngleLeft className="w-7 h-7" />
        </button>
        <p className="text-lg font-semibold truncate max-w-xs">
          {test.test_name}
        </p>
        <div className="w-7 h-7" />
      </div>

      <div className="relative w-full h-4 bg-gray-700 rounded-full overflow-hidden mt-4">
        <div
          className={`h-full relative bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move transition-all duration-300 ${
            isMaxed ? "animate-ping" : ""
          }`}
          style={{ width: `${progress}%` }}
        >
          {sparks.map((spark) => (
            <div
              key={spark.id}
              className="absolute w-1 h-1 bg-white rounded-full opacity-75 animate-ping"
              style={{ top: spark.y, left: spark.x }}
            ></div>
          ))}
        </div>
      </div>
      <p className="text-center text-sm text-gray-400 mt-2">
        {currentQuestionIndex + 1} / {test.items.length}
      </p>

      <div className="flex-1 flex flex-col justify-center px-4">
        <div className="w-full max-w-2xl mx-auto">
          <p className="mb-6 text-lg font-medium text-white text-center">
            {currentItem.question}
          </p>
          <div className="flex flex-col space-y-4">
            {currentItem.options.map((option, index) => {
              const isSelected = selectedOptionIndex === index;
              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(currentItem.id, index)}
                  className={`flex items-center py-3 px-4 rounded-lg border ${
                    isSelected
                      ? "bg-emerald-900 border-emerald-500"
                      : "bg-gray-800 border-gray-600"
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-4 h-4 mr-3 rounded-full border ${
                      isSelected
                        ? "bg-emerald-500 border-emerald-500"
                        : "border-gray-600"
                    }`}
                  />
                  <span className="text-white text-left">{option.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-between w-full gap-4 mb-4">
        <button
          onClick={goToPrevious}
          disabled={currentQuestionIndex === 0}
          className="py-2 px-4 bg-gray-800 rounded-lg disabled:opacity-50"
        >
          <FaChevronLeft />
        </button>
        <button
          onClick={goToNext}
          disabled={selectedOptionIndex === undefined}
          className={`flex-1 py-2 text-3xl font-bold rounded-lg ${
            selectedOptionIndex === undefined
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move"
          }`}
        >
          {currentQuestionIndex === test.items.length - 1
            ? t("tests.finish")
            : t("tests.next")}
        </button>
      </div>

      {showExitDialog && (
        <ConfirmDialog
          status="warning"
          title="tests.exitTitle"
          message="tests.exitMessage"
          confirmText={t("tests.exitConfirm")}
          cancelText={t("tests.exitCancel")}
          onConfirm={confirmExit}
          onCancel={cancelExit}
        />
      )}

      {showPremium && (
        <Premium showPremium={showPremium} setShowPremium={setShowPremium} />
      )}
    </div>
  );
}

export default Test;
